import { useState } from 'react'
import { useApp } from '@/store/app'
import { CardRow } from '@/components/CardRow'
import { ViewHeader } from '@/components/AppShell'

/**
 * Katalog — für den Fall, dass jemand nicht weiß, wonach er suchen soll.
 *
 * Nach Kategorien gruppiert, alle Gruppen anfangs geöffnet: Ein zugeklapptes
 * Verzeichnis verlangt einen zusätzlichen Griff, bevor man überhaupt sieht, was es gibt.
 *
 * Was danach zugeklappt wurde, bleibt es. Wer die Rettungsdienst-Karten nicht braucht,
 * klappt sie einmal weg und nicht bei jedem Öffnen erneut.
 */
const SPEICHER = 'taschenkarten:katalog-zugeklappt'

/**
 * Gespeichert werden die **zugeklappten** Kategorien, nicht die offenen. So ist der
 * Standard „alles offen" auch für Kategorien gültig, die es beim letzten Besuch noch
 * nicht gab — eine neu hinzugekommene Rubrik zeigt sich, statt sich zu verstecken.
 */
function leseZugeklappt(): ReadonlySet<string> {
  try {
    const roh = localStorage.getItem(SPEICHER)
    if (!roh) return new Set()
    const werte: unknown = JSON.parse(roh)
    if (!Array.isArray(werte)) return new Set()
    return new Set(werte.filter((wert): wert is string => typeof wert === 'string'))
  } catch {
    // Privater Modus ohne Speicherzugriff oder ein beschädigter Eintrag. Beides ist
    // kein Grund, den Katalog nicht anzuzeigen — dann eben mit allen Gruppen offen.
    return new Set()
  }
}

function merkeZugeklappt(ids: ReadonlySet<string>): void {
  try {
    localStorage.setItem(SPEICHER, JSON.stringify([...ids]))
  } catch {
    // Kein Speicherzugriff: Die Ansicht gilt dann nur für diese Sitzung.
  }
}

export function CatalogView() {
  const catalog = useApp((s) => s.catalog)
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(leseZugeklappt)

  if (!catalog) return null

  const toggle = (id: string) => {
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      merkeZugeklappt(next)
      return next
    })
  }

  return (
    <div>
      <ViewHeader title="Katalog" hint={`${catalog.cards.length} Karten`} />

      {catalog.categories.map((category) => {
        const cards = catalog.cards.filter((card) => card.category === category.id)
        if (cards.length === 0) return null
        const isOpen = !collapsed.has(category.id)

        return (
          <section key={category.id} className="mb-2">
            {/* Rubrikleiste: eigene Fläche, Schrift und Pfeil in der Akzentfarbe. Sie
                muss beim Überfliegen als Gliederung erkennbar sein und nicht als
                weiterer Eintrag — sonst sucht man die Kategorie in der Liste der
                Karten. Der Grad ist bewusst 20 px: In der Akzentfarbe auf hellgrauer
                Fläche liegt der Kontrast bei 4,45:1, was ab dieser Größe genügt. */}
            <button
              type="button"
              onClick={() => toggle(category.id)}
              aria-expanded={isOpen}
              className="tap flex w-full items-center justify-between gap-3 rounded-card
                         bg-section px-4 text-left transition-colors duration-100
                         hover:bg-raised"
            >
              <span>
                <span className="auf-akzent block text-xl font-bold uppercase tracking-wide text-accent">
                  {category.label}
                </span>
                {category.description && (
                  <span className="block text-xs text-muted">{category.description}</span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-3 text-sm font-semibold text-accent">
                {cards.length}
                <span
                  aria-hidden
                  className={
                    'inline-block size-2.5 border-b-2 border-r-2 border-current transition-transform duration-150 ' +
                    (isOpen ? 'rotate-45' : '-rotate-45')
                  }
                />
              </span>
            </button>

            {isOpen && cards.map((card) => <CardRow key={card.id} card={card} />)}
          </section>
        )
      })}
    </div>
  )
}
